import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface GachaCard {
  cardId: string;
  scpCode: string;
  name: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  imageUrl: string | null;
  isDuplicate: boolean;
  fragments: number;
  stats: Record<string, number>;
}

interface GachaAnimationProps {
  cards: GachaCard[];
  onComplete: () => void;
  title?: string;
}

const RARITY_CONFIG = {
  common: {
    label: 'Common',
    color: 'border-gray-400',
    bg: 'bg-gray-100 dark:bg-gray-800',
    glow: '',
    text: 'text-gray-600 dark:text-gray-400',
    delay: 0.3,
  },
  rare: {
    label: 'Rare',
    color: 'border-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-950',
    glow: 'shadow-[0_0_20px_rgba(59,130,246,0.3)]',
    text: 'text-blue-600 dark:text-blue-400',
    delay: 0.5,
  },
  epic: {
    label: 'Epic',
    color: 'border-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-950',
    glow: 'shadow-[0_0_30px_rgba(168,85,247,0.4)]',
    text: 'text-purple-600 dark:text-purple-400',
    delay: 0.8,
  },
  legendary: {
    label: 'Legendary',
    color: 'border-yellow-500',
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    glow: 'shadow-[0_0_40px_rgba(234,179,8,0.5)]',
    text: 'text-yellow-600 dark:text-yellow-400',
    delay: 1.2,
  },
};

/**
 * 단일 카드 공개 애니메이션
 */
function CardReveal({ card, index, onRevealed }: { card: GachaCard; index: number; onRevealed: () => void }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const config = RARITY_CONFIG[card.rarity];
  const staggerDelay = index * 0.15;

  // onRevealed를 ref로 안정화하여 useEffect 재실행 방지
  const onRevealedRef = useRef(onRevealed);
  onRevealedRef.current = onRevealed;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFlipped(true);
      setTimeout(() => onRevealedRef.current(), 500);
    }, staggerDelay * 1000 + config.delay * 1000);

    return () => clearTimeout(timer);
  }, [staggerDelay, config.delay]);

  return (
    <motion.div
      initial={{ scale: 0, rotateY: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: staggerDelay, type: 'spring', stiffness: 200 }}
      className="perspective-1000"
    >
      <motion.div
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, type: 'spring' }}
        className="relative w-full aspect-[3/4]"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* 카드 뒷면 */}
        <div
          className="absolute inset-0 rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="text-4xl">🎴</div>
        </div>

        {/* 카드 앞면 */}
        <div
          className={cn(
            'absolute inset-0 rounded-xl border-2 overflow-hidden',
            config.color, config.bg, config.glow,
          )}
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          {/* 이미지 */}
          <div className="relative aspect-square">
            {card.imageUrl ? (
              <img src={card.imageUrl} alt={card.name} loading="lazy" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center text-3xl">🎭</div>
            )}

            {/* 등급 배지 */}
            <div className={cn('absolute top-1.5 right-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold', config.bg, config.text)}>
              {config.label}
            </div>

            {/* 중복 표시 */}
            {card.isDuplicate && (
              <div className="absolute bottom-1.5 left-1.5 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                중복 +{card.fragments}조각
              </div>
            )}
          </div>

          {/* 카드 정보 */}
          <div className="p-2 text-center">
            <p className="font-bold text-xs truncate">{card.scpCode}</p>
            <p className="text-[10px] text-muted-foreground truncate">{card.name}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/**
 * 뽑기 결과 애니메이션 (1회 or 10연차)
 */
export function GachaAnimation({ cards, onComplete, title }: GachaAnimationProps) {
  const [revealedCount, setRevealedCount] = useState(0);
  const allRevealed = revealedCount >= cards.length;

  // 최고 등급 판별 (연출용)
  const highestRarity = cards.reduce((best, card) => {
    const order = { legendary: 4, epic: 3, rare: 2, common: 1 };
    return order[card.rarity] > order[best] ? card.rarity : best;
  }, 'common' as GachaCard['rarity']);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (allRevealed) { e.stopPropagation(); onComplete(); } }}
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        className="min-h-full flex items-center justify-center p-4 py-8"
        onClick={(e) => e.stopPropagation()}
      >
      <div className="w-full max-w-2xl space-y-6">
        {/* 타이틀 */}
        {title && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white text-lg font-semibold"
          >
            {title}
          </motion.p>
        )}

        {/* 배경 이펙트 (등급별) */}
        {highestRarity === 'legendary' && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-yellow-500/10 to-transparent pointer-events-none"
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
        )}
        {highestRarity === 'epic' && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-purple-500/10 to-transparent pointer-events-none"
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ repeat: Infinity, duration: 2.5 }}
          />
        )}

        {/* 카드 그리드 — 모바일에서 2열 유지로 가독성 확보 */}
        <div className={cn(
          'grid gap-2 sm:gap-3',
          cards.length === 1 ? 'grid-cols-1 max-w-[200px] mx-auto' : 'grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5',
        )}>
          {cards.map((card, i) => (
            <CardReveal
              key={`${card.cardId}_${i}`}
              card={card}
              index={i}
              onRevealed={() => setRevealedCount(prev => prev + 1)}
            />
          ))}
        </div>

        {/* 결과 요약 (모두 공개 후) */}
        <AnimatePresence>
          {allRevealed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center space-y-3"
            >
              <p className="text-white/80 text-sm">
                새 카드 {cards.filter(c => !c.isDuplicate).length}장
                {cards.some(c => c.isDuplicate) && ` · 중복 ${cards.filter(c => c.isDuplicate).length}장 (+${cards.reduce((s, c) => s + c.fragments, 0)}조각)`}
              </p>
              <button
                onClick={onComplete}
                className="min-h-[44px] px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm transition-colors"
              >
                확인
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </motion.div>
    </motion.div>
  );
}
